import * as authPopup from '@/auth/authPopup'
import * as authRedirect from '@/auth/authRedirect'
import {callApi, storeRegistrationByEventIdAndAttendeeId} from '@/shared/ApiHelper'
import {EventType} from '@azure/msal-browser'
import {useApiStore} from '@/store/api.js'
import {useMsalStore} from "@/store/msal"

// Pinia stores are resolved lazily so this module is safe to static-import
// before app.use(pinia) runs. The Proxy preserves every existing
// `apiStore.x` / `msalStore.x` call site, including `this`-bound actions.
function lazyStore(useStoreFn) {
    let resolved
    return new Proxy({}, {
        get(_, key) {
            if (!resolved) resolved = useStoreFn()
            return resolved[key]
        },
        set(_, key, value) {
            if (!resolved) resolved = useStoreFn()
            resolved[key] = value
            return true
        },
    })
}
const apiStore = lazyStore(useApiStore)
const msalStore = lazyStore(useMsalStore)

let currentClickHandler = null

// Registers the LOGIN_SUCCESS handler on the MSAL agent. Called from main.js
// after initMsal() so the agent exists; previously ran at module-eval, which is
// no longer safe now that the agent is created lazily (events-app#913).
export function registerSignInCallback() {
    authPopup.msalAgent.addEventCallback((message) => {
        if (message.eventType == EventType.LOGIN_SUCCESS) {
            checkForSignIn()
            // Workaround for a bug where some async part of the sign in process never resolves, sometimes preventing sign out
            if (message.eventType == EventType.LOGIN_SUCCESS) {
                window.location = window.location.href
                window.location.reload() // I'm not a fan of this but this fires after the HomeView does its checkForSign in
                // which is within the mounted and will not trigger a events list refresh
            }
        }
    })
}

export const checkForSignIn = async function () {
    if( msalStore.accessToken ){
        // Token exists - assume valid and let API error handling catch expired tokens.
        // On a direct load / hard-refresh the MSAL token can be restored without the
        // ACQUIRE_TOKEN_SUCCESS handler having populated apiStore.oid, which leaves the
        // /profile gate seeing a null oid and wrongly showing "you must sign in". Derive
        // oid from the signed-in account here, mirroring that handler in authPopup.js
        // (apiStore.oid = account.localAccountId) so a restored token is recognized
        // (events-app#981).
        if (null == apiStore.oid) {
            const account = authPopup.msalAgent.getActiveAccount()
                || authPopup.msalAgent.getAllAccounts()[0]
            if (account) {
                apiStore.oid = account.localAccountId
            }
        }
        if (null == apiStore.currentAttendee) {
            await reloadAttendee()
        }
        await setSigninSignOutLink('signout')
    } else {
        const b2cTokenData = await authRedirect.passTokenToApi().catch(
            (error) => console.error(error)
        )
        if(b2cTokenData.accessToken){
            // Use B2C access token directly for API authentication
            // Token is already set in msalStore by passTokenToApi()
            apiStore.events = null // this ensures we refresh the events when we sign in (is checked in HomeView)
            apiStore.oid = b2cTokenData.uniqueId
            await updateIdentityProvider(b2cTokenData)
            console.log('last_identity_provider updated')
            await reloadAttendee()
            await setSigninSignOutLink('signout')
        } else {
            await setSigninSignOutLink('signin')
        }
    }
    return apiStore.oid
}

export const reloadAttendee = async function() {
    if (apiStore.oid) {
        const apiResponse = await callApi(apiStore.buildUrl('/events/attendees/?a=' + apiStore.oid), 'GET', {}, true)
        if (apiResponse) {
            const attendee = apiResponse.data[0]
            if (attendee.is_active) {
                apiStore.currentAttendee = attendee
                apiStore.displayName = attendee.display_name
                if (apiStore.currentEvent) {
                    await storeRegistrationByEventIdAndAttendeeId(apiStore.currentEvent.event.id, attendee.id)
                } else {
                    apiStore.currentRegistration = null
                }
            } else {
                authRedirect.signOut()
            }
        }
    }
}

export const setDisplayName = async function (displayName, email = null) {
    if (undefined == displayName || undefined == email || null == email) {
        if (apiStore.displayName) {
            displayName = apiStore.displayName
            email = apiStore.currentAttendee.registration_email_address
        } else {
            if (apiStore.oid) {
                try {
                    await reloadAttendee()
                    if (apiStore.currentAttendee) {
                        displayName = apiStore.currentAttendee.display_name
                        email = apiStore.currentAttendee.registration_email_address
                    }
                } catch(exception) {
                    console.warn(exception)
                    apiStore.displayName = null
                    const accounts = authPopup.msalAgent.getAllAccounts()
                    if (0 < accounts.length) {
                        apiStore.displayName = accounts[0].idTokenClaims['name']
                    }
                }
            }
        }
    } else {
        apiStore.displayName = displayName
    }

    if (undefined == displayName) {
        displayName = 'Not signed in'
        email = null
    } else {
        apiStore.displayName = displayName
    }
    document.querySelector('#accountNameText').textContent = displayName
    document.querySelector('#accountEmailText').textContent = email
}

export async function setSigninSignOutLink(linkType){
    if(linkType === 'signout'){
        const email = apiStore.currentAttendee ? apiStore.currentAttendee.registration_email_address : null
        await setLink('Sign out', apiStore.displayName, authRedirect.signOut, true, email)
    }
    if(linkType === 'signin'){
        await setLink('Sign in/Sign up', 'Not signed in', authRedirect.signIn, false)
        apiStore.displayName = null
        apiStore.isStaff = false
        apiStore.oid = null
        msalStore.accessToken = ''
    }
}

async function setLink(linkText, displayName, clickHandler, isSignOut, email = null) {
    document.querySelector('.loginLinkText').textContent = linkText
    const loginLink = document.querySelector('.loginLink')
    if (currentClickHandler) {
        loginLink.removeEventListener('click', currentClickHandler)
    }
    currentClickHandler = clickHandler
    loginLink.addEventListener('click', clickHandler)
    await setDisplayName(displayName, email)
    if (isSignOut) {
        document.querySelector('#accountLinks').style.display = 'block'
    } else {
        document.querySelector('#accountLinks').style.display = 'none'
    }
}

async function updateIdentityProvider(userData) {
    // async call to api to set the last_identity_provider on the attendee that just logged in
    let apiStore = useApiStore()
    let identity_provider;
    if( userData.account.idTokenClaims.currentUserIdentities && userData.account.idTokenClaims.currentUserIdentities.length > 0){
        let identity_provider_raw = userData.account.idTokenClaims.currentUserIdentities[0].Issuer
        switch (true) {
            case /microsoft/g.test(identity_provider_raw):
                identity_provider = 'microsoft'
                break;
            case /google/g.test(identity_provider_raw):
                identity_provider = 'google'
                break;
            case /apple/g.test(identity_provider_raw):
                identity_provider = 'apple'
                break;
            default:
                identity_provider = 'unknown'
        }
    } else {
        identity_provider = 'local'
    }

    const endpoint = apiStore.buildUrl('/events/attendees/')
    const data = {last_identity_provider: identity_provider, id: userData.uniqueId}
    callApi(endpoint, 'PATCH', data )
        .then( () => console.log('identity provider updated') )
        .catch(error => {
            console.log('failed to update identity provider')
            console.error(error)
        })

}